import type { RxInput } from '~/api/graphql/generated/types'
import type { Order, Prescription } from '~/types'
import {
  convertOrderToRxInput,
  getPrescriptionAutoshipFrequency,
  getPrescriptionAutoshipUnitId,
} from '~/utils/prescription'

// Extended Order type for search results that include prescription-specific fields
type SearchResultOrder = Order & {
  administrationQuantityUnitId?: string
  categoryId?: string
  drugInfo?: Prescription['drugInfo']
  drugMasterName?: string
  globalInventoryId?: string
  globalInventoryMappingId?: string
  globalVariationId?: string
  globalVariationMappingId?: string
  prescriptionType?: Prescription['prescriptionType']
  rxType?: Prescription['rxType']
}

export const sanitizePrescriptionForSwap = (
  prescription: Prescription,
): Prescription => {
  const {
    id,
    createdAt,
    updatedAt,
    deleted,
    rxNo,
    prescribedDate,
    inventory,
    inventoryId,
    variationId,
    ...rest
  } = prescription

  const variation =
    prescription.globalVariationMapping || prescription.globalVariation

  return {
    ...rest,
    variation,
  } as Prescription
}

/**
 * Converts a search result Order to a Prescription for swap flow
 * Starts with sanitized base prescription and overrides with search result data
 */
export const convertSearchOrderToPrescription = (
  searchOrder: Order,
  basePrescription: Prescription,
): Prescription => {
  // Start with sanitized base prescription to preserve all common fields
  // (quantity, frequency, doctor, refills, etc.)
  const sanitizedBase = sanitizePrescriptionForSwap(basePrescription)

  // Cast to extended type that includes prescription-specific fields from search results
  const order = searchOrder as SearchResultOrder

  return {
    ...sanitizedBase,

    // Override with product details from search result
    name: order.name,
    categoryId: order.categoryId,
    drugMasterName: order.drugMasterName,
    drugInfo: order.drugInfo,

    // Override with global inventory/variation IDs for pricing lookup
    // Extract IDs from objects if direct ID fields are not available
    globalInventoryId: order.globalInventoryId || order.globalInventory?.id,
    globalInventoryMapping: order.globalInventoryMapping,
    globalInventoryMappingId:
      order.globalInventoryMappingId || order.globalInventoryMapping?.id,
    globalVariationId: order.globalVariationId || order.globalVariation?.id,
    globalVariationMapping: order.globalVariationMapping,
    globalVariationMappingId:
      order.globalVariationMappingId || order.globalVariationMapping?.id,
    // Also copy the objects themselves for workflow detection
    globalInventory: order.globalInventory,
    globalVariation: order.globalVariation,

    // Override with administration unit from search result if available
    administrationQuantityUnitId:
      order.administrationQuantityUnitId ||
      sanitizedBase.administrationQuantityUnitId,

    // Override rxType if available in search result
    rxType: order.rxType || sanitizedBase.rxType,

    // Set prescription type to CHEWY
    prescriptionType: order.prescriptionType || 'CHEWY',

    // Clear partNumber - user must select a new Chewy item for the new product
    partNumber: undefined,
  } as Prescription
}

export const convertPrescriptionToRxInputForSwap = (
  prescription: Prescription,
  isFoodCatalogEnabled: boolean,
): RxInput => {
  const autoshipUnitId = getPrescriptionAutoshipUnitId(prescription)
  const autoshipFrequency = getPrescriptionAutoshipFrequency(prescription)

  return convertOrderToRxInput(
    prescription,
    isFoodCatalogEnabled,
    autoshipUnitId,
    autoshipFrequency,
  )
}
